import React, { useState } from 'react';
import { cn } from 'src/util/cn';
import InputError from './InputError';
import { RenderIcon } from 'src/assets/icons';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helperText?: string;
  prefix?: '$';
  errorState?: boolean;
  placeholder?: string;
}
export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
}

const fieldsetBaseClass =
  'bg-gray-80 rounded-[46px] has-[:disabled]:opacity-50 py-[6px] px-[15px] group text-[13px] sm:text-[14px] flex gap-2 items-center border border-gray-80 justify-center relative transition-all';

const inputBaseClass =
  'peer h-full w-full text-[13px] sm:text-[14px] bg-transparent mt-4 font-sans font-normal font-popins text-blue-gray-700 outline outline-0 transition-all focus:outline-0 placeholder:opacity-0 focus:placeholder:opacity-100';

const helperTextBaseClass =
  'text-xs text-gray-60 !m-0 pt-1 max-h-0 overflow-hidden group-hover:max-h-32 transition-all duration-500';

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, errorState, helperText, prefix,style, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    return (
      <div className="group">
        <fieldset
          className={cn(
            fieldsetBaseClass,
            className,
            (props.pattern || props.min || props.max) &&
              'has-[input:invalid]:border-red',
            error ? 'border-red-500 border-solid mb-4' : 'mb-0',
            errorState && 'border-red-500 border-solid'
          )}
        >
          <input
            placeholder={props.placeholder || ''}
            type={showPassword && type === 'password' ? 'text' : type}
            className={inputBaseClass}
            ref={ref}
            {...props}
            style={{...style, border: "none", boxShadow: "none" }}
          />
          <label
            className={cn(
              `pointer-events-none absolute top-[32%] -translate-y-1/2 flex select-none !overflow-visible truncate text-[10px] sm:text-[12px] 
              font-normal text-gray-60 transition-all group-hover:text-white
              peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[50%]
              peer-focus:text-[10px] sm:peer-focus:text-[12px] peer-focus:top-[32%]`,
              'left-4',
              error && 'text-red group-hover:text-red'
            )}
          >
            {props.label}
          </label>
          {type === 'password' && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 flex items-center transform -translate-y-1/2 text-gray-60 hover:text-white bg-transparent border-none"
            >
              {showPassword? <RenderIcon type='closeEyeIcon' className='opacity-60 hover:opacity-100' />:
               <RenderIcon type='openEyeIcon' className='opacity-60 hover:opacity-100' />}
            </button>
          )}
        </fieldset>
        {helperText && <p className={helperTextBaseClass}>{helperText}</p>}
        <InputError error={error} />
      </div>
    );
  }
);
Input.displayName = 'Input';

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, ...props }, ref) => {
    return (
      <>
        <fieldset
          className={cn(
            fieldsetBaseClass,
            className,
            error ? 'border-red mb-4' : 'mb-0'
          )}
        >
          <textarea
            className={inputBaseClass}
            ref={ref}
            placeholder={label}
            {...props}
          />
          <label
            className={cn(
              `pointer-events-none absolute top-[10px] left-4 -translate-y-1/2 flex 
          select-none !overflow-visible truncate text-[10px] 
          font-normal text-gray-60 transition-all group-hover:text-white
          peer-placeholder-shown:text-sm peer-placeholder-shown:top-[50%]
          peer-focus:text-[10px] peer-focus:top-[10px]`,
              error && 'text-red'
            )}
          >
            {label}
          </label>
        </fieldset>
        <InputError error={error} />
      </>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Input, Textarea };
